"use client";

import { Button } from "@/components/ui/button";
import { useAppleSignIn } from "@/hooks/use-apple-sign-in";
import { useGoogleSignIn } from "@/hooks/use-google-sign-in";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import type { ComponentProps } from "react";
import { toast } from "sonner";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4 fill-current">
    <path d="M17.05 20.28c-.98.95-2.05.86-3.08.41-1.09-.47-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.41C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8.98-.2 1.92-.86 3.24-.77 1.58.13 2.77.75 3.55 1.92-3.27 1.96-2.5 6.27.49 7.48-.6 1.57-1.37 3.12-2.36 3.54zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

export interface GoogleSignInButtonProps extends ComponentProps<"div"> { withDivider?: boolean }

function ProviderButton({ provider, withDivider = true, className, ...props }:
  GoogleSignInButtonProps & { provider: "google" | "apple" }) {
  const t = useTranslations();
  const locale = useParams().locale as string;
  const google = useGoogleSignIn(locale);
  const apple = useAppleSignIn(locale);
  const auth = provider === "google" ? google : apple;
  if (!auth.isAvailable) return null;
  const label = auth.isPending ? t("auth.social.redirecting") :
    t(provider === "google" ? "auth.social.continueWithGoogle" : "auth.social.continueWithApple");
  return (
    <div className={cn("flex flex-col gap-3", className)} {...props}>
      {withDivider && <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-muted-foreground text-xs uppercase tracking-wide">{t("auth.social.divider")}</span>
        <span className="h-px flex-1 bg-border" />
      </div>}
      <Button type="button" variant={provider === "google" ? "outline" : "default"}
        className={cn("w-full", provider === "apple" && "bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90")}
        disabled={auth.isPending} aria-label={label} aria-busy={auth.isPending}
        onClick={async () => { try { await auth.start(); } catch { toast.error(t("auth.social.failed", { provider: provider === "google" ? "Google" : "Apple" })); } }}>
        {provider === "google" ? <GoogleIcon /> : <AppleIcon />}{label}
      </Button>
    </div>
  );
}

export function GoogleSignInButton(props: GoogleSignInButtonProps) {
  return <ProviderButton provider="google" {...props} />;
}
export function AppleSignInButton(props: GoogleSignInButtonProps) {
  return <ProviderButton provider="apple" {...props} />;
}
